import { COMPASS_PLACES_MOST, MINIMAP_FIELD, MINIMAP_SCALES, createMinimapOthers, minimapMarks, minimapScale, parseMinimapStep, resetMinimapOthers, stepMinimapZoom, type MinimapMark, type MinimapOthers, type MinimapPlaceSpot, type MinimapSpot, type Size } from "odyssey-shared";
import type { MapPictureSource } from "../map-picture/pictures.ts";
import type { LiveParty, PartySeat } from "../party/live-spots.ts";
import { PartyMarks } from "../party/party-marks.ts";
import { localStore, readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import { COMPASS_PLACES_KEY, parseCompassPlaces } from "./places-setting.ts";

/**
 * The minimap's store (P1.9-T52, R-0055; ADR-061 decision 1: a DOM frame with canvas contents): the zoom step and the
 * marks the fields draw, owned by the game rather than by React. It tells its
 * listeners only when a value they show changes, so walking draws nothing in React: the player's position goes to
 * `setPosition`, which the painter hears through `onMove` and the snapshot never carries. The zoom is kept
 * in this browser's `localStorage`, per device. It also holds the map's pictures the fields draw their ground from
 * (P1.9-T178), and each position it is given steps their composition, so a chunk that lands is heard as a move.
 *
 * Other players and creatures (P1.9-T179) are not in the snapshot: they change every frame, so they live in one
 * preallocated buffer (`MinimapOthers`) the scene refills each frame between `beginOthers` and `endOthers`, and the
 * painters read it when told, never React. `endOthers` only notes whether what the buffer holds changed; the painters
 * hear it in the same frame's `setPosition`, once.
 *
 * A party member the scene draws is not one of those dots but the party's own mark, placed where the member is drawn
 * (P2.12-T35): the scene hands the drawn members over with `setLiveParty` each frame, and the party's marks are the
 * `partyPositions` feed's members resolved against them (`PartyMarks`).
 */

/**
 * The storage key the zoom step lives under.
 */
export const MINIMAP_STEP_KEY = "odyssey.minimap.step";

/**
 * The storage key the Nearby window's expanded flag lived under, which nothing reads any more (P2.12-T22, ADR-100
 * decision 44): the page writes the empty text over it at its start.
 */
export const MINIMAP_EXPANDED_KEY = "odyssey.minimap.expanded";

/**
 * The storage key the Show coordinates choice lives under (P1.9-T197): per device.
 */
export const MINIMAP_COORDINATES_KEY = "odyssey.minimap.coordinates";

/**
 * Reads a stored value as the Show coordinates choice, on by default (the coordinator's default, easily changed).
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns False for exactly `off`, true for anything else.
 */
export function parseMinimapCoordinates(raw: string | null): boolean {
  return raw !== "off";
}

/**
 * One field of the minimap: its size in logical pixels, its scale in logical pixels a tile and the marks in it.
 */
export interface MinimapField {
  readonly size: Size;
  readonly scale: number;
  readonly marks: readonly MinimapMark[];
}

/**
 * What the minimap's view reads: the zoom step shared by both fields, whether each direction of zoom has a step left,
 * the HUD's field and the Map window's Nearby field (P2.12-T21) while the Map asks for one, the name of the place the line
 * under the field carries (`place`, empty before the page gives one) and whether that line shows (`coordinates`,
 * P1.9-T197).
 */
export interface MinimapSnapshot {
  readonly step: number;
  readonly canZoomIn: boolean;
  readonly canZoomOut: boolean;
  readonly party: boolean;
  readonly place: string;
  readonly coordinates: boolean;
  readonly hud: MinimapField;
  readonly map: MinimapField | null;
}

/**
 * What the minimap's view reads, and a way to hear that it changed.
 */
export interface MinimapSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => MinimapSnapshot;
}

/**
 * What the scene tells the minimap: the player's position every frame, and the party members it may draw.
 */
export interface MinimapFeed {
  /**
   * The buffer of other players and creatures the painters read, which holds the last finished frame's marks.
   */
  readonly others: MinimapOthers;
  /**
   * Sets the player's position.
   *
   * @param x Tiles across.
   * @param y Tiles down.
   */
  setPosition(x: number, y: number): void;
  /**
   * Sets who besides the player the field may draw; each is drawn only within vision, and the compass band points to
   * the ones the field draws no mark for. The painters hear a change as they hear a move.
   *
   * @param spots The party members' positions in tiles, as the `partyPositions` feed last gave them.
   * @param seats The same members' names, which the scene matches its drawn characters against.
   */
  setParty(spots: readonly MinimapSpot[], seats?: readonly PartySeat[]): void;
  /**
   * The names of the party members on the player's map, as last given with `setParty`.
   *
   * @returns The seats; the same array until another is given.
   */
  partySeats(): readonly PartySeat[];
  /**
   * Takes the party members the scene drew this frame, at their drawn positions; each one's mark stands there rather
   * than at the feed's position, and one no longer drawn keeps the last until the next `setParty` (`rememberLiveParty`).
   *
   * @param live The members drawn, the same buffer every frame.
   */
  setLiveParty(live: LiveParty): void;
  /**
   * Sets the player's pin, the point they dropped on the Map to say where they are going (ADR-100 decision 11), or
   * none. The HUD field draws its teardrop while the pin's centre is inside the field, and the compass band a pip
   * while it is not; Nearby draws neither, the Map's own pin standing in it.
   *
   * @param pin The pin in tiles, or null for none.
   */
  setPin(pin: { readonly x: number; readonly y: number } | null): void;
  /**
   * Sets the places the character knows, each a diamond in the field where its centre falls inside it and a pip on the
   * compass band where it does not and is among the nearest ones the Places on the compass choice allows (P1.9-T189).
   * Only discovered places are given; nothing here can name one that is not.
   *
   * @param spots The places in tiles.
   */
  setPlaces(spots: readonly MinimapPlaceSpot[]): void;
  /**
   * Sets the name of the place the line under the field carries (P1.9-T197): the map's region, else the map's name.
   *
   * @param name The name, empty for none.
   */
  setPlace(name: string): void;
  /**
   * Gives the map's pictures the fields draw their ground from, or takes them away.
   *
   * @param ground The pictures, or null.
   */
  setGround(ground: MapPictureSource | null): void;
  /**
   * Empties the buffer of other players and creatures for a frame and hands it over to be refilled
   * (`addMinimapOther`); the frame ends with `endOthers`.
   *
   * @returns The buffer, the same one every frame.
   */
  beginOthers(): MinimapOthers;
  /**
   * Ends a frame's refill of the buffer: notes whether what it holds differs from the frame before, which the next
   * `setPosition` tells the painters.
   */
  endOthers(): void;
}

/**
 * Forgets the Nearby window's saved expanded flag (ADR-100 decision 44): the empty text is written over it, as a key is
 * cleared from a store that has no way to remove one. Nothing reads the key again, so a page that had it set opens no
 * window.
 *
 * @param store Where the flag was kept, or null for none.
 */
export function forgetMinimapExpanded(store: SettingsStore | null = localStore()): void {
  writeSetting(MINIMAP_EXPANDED_KEY, "", store);
}

/**
 * Whether two lists of marks draw the same.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when each mark matches its counterpart.
 */
function sameMarks(a: readonly MinimapMark[], b: readonly MinimapMark[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] as MinimapMark;
    const right = b[i] as MinimapMark;
    if (left.kind !== right.kind || left.handle !== right.handle || left.name !== right.name || left.place !== right.place || left.left !== right.left || left.top !== right.top || left.size !== right.size) {
      return false;
    }
  }
  return true;
}

/**
 * The minimap's store.
 *
 * @example
 * const minimap = new MinimapStore(localStore());
 * minimap.setPosition(120.5, 88);
 */
export class MinimapStore implements MinimapSource, MinimapFeed {
  private step: number;
  private place = "";
  private coordinates: boolean;
  private px = 0;
  private py = 0;
  private readonly partyMarks = new PartyMarks();
  private pinHeld: { readonly x: number; readonly y: number } | null = null;
  private placesHeld: readonly MinimapPlaceSpot[] = [];
  private compassCount: number;
  private mapSize: Size | null = null;
  private ground: MapPictureSource | null = null;
  private readonly marksOthers = createMinimapOthers();
  private heldOthers: number;
  private othersChanged = false;
  private snapshot: MinimapSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly movers = new Set<() => void>();

  /**
   * @param store Where the zoom is kept, or null for none; this browser's `localStorage` when omitted.
   */
  constructor(private readonly store: SettingsStore | null = localStore()) {
    this.step = readSetting(MINIMAP_STEP_KEY, parseMinimapStep, store);
    this.coordinates = readSetting(MINIMAP_COORDINATES_KEY, parseMinimapCoordinates, store);
    this.compassCount = readSetting(COMPASS_PLACES_KEY, parseCompassPlaces, store);
    this.snapshot = this.build();
    this.heldOthers = this.marksOthers.signature;
  }

  /**
   * The player's position across, in tiles.
   *
   * @returns Tiles.
   */
  public get x(): number {
    return this.px;
  }

  /**
   * The player's position down, in tiles.
   *
   * @returns Tiles.
   */
  public get y(): number {
    return this.py;
  }

  /**
   * The buffer of other players and creatures the painters read; it holds the last finished frame's marks.
   *
   * @returns The buffer.
   */
  public get others(): MinimapOthers {
    return this.marksOthers;
  }

  /**
   * The map's pictures, or null before the map is booted.
   *
   * @returns The pictures.
   */
  public get pictures(): MapPictureSource | null {
    return this.ground;
  }

  /**
   * Listens for the snapshot changing; the same function for the store's life, so React subscribes once.
   *
   * @param listener Called after each change.
   * @returns Stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The current snapshot, kept until a value changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): MinimapSnapshot => this.snapshot;

  /**
   * Listens for the player's position being set, which the canvas painter draws the ground from.
   *
   * @param listener Called after each position.
   * @returns Stops listening.
   */
  public onMove(listener: () => void): () => void {
    this.movers.add(listener);
    return () => {
      this.movers.delete(listener);
    };
  }

  /**
   * Steps the zoom in, to a larger scale, and keeps it.
   */
  public zoomIn(): void {
    this.setStepTo(stepMinimapZoom(this.step, "in"));
  }

  /**
   * Steps the zoom out, to a smaller scale, and keeps it.
   */
  public zoomOut(): void {
    this.setStepTo(stepMinimapZoom(this.step, "out"));
  }

  /**
   * Asks for the Map window's Nearby field at a size, or says it is no longer wanted. The field is the Nearby scale at the
   * step the minimap keeps, its marks the same rule's (the player in the middle, party members inside vision only,
   * however far the field reaches), so a larger field shows more ground and no more marks.
   *
   * @param size The field's size in logical pixels, or null, which frees the `nearby` picture (decision 42).
   */
  public setMapField(size: Size | null): void {
    if (size === this.mapSize || (size !== null && this.mapSize !== null && size.width === this.mapSize.width && size.height === this.mapSize.height)) {
      return;
    }
    const stopped = size === null && this.mapSize !== null;
    this.mapSize = size === null ? null : { width: size.width, height: size.height };
    if (stopped) {
      this.ground?.release("nearby");
    }
    this.publish();
  }

  /**
   * Sets the zoom step directly, as the Map's zoom does, and keeps it.
   *
   * @param step The step, 0 to 2.
   */
  public setStep(step: number): void {
    this.setStepTo(Math.max(0, Math.min(MINIMAP_SCALES.length - 1, Math.floor(step))));
  }

  /**
   * Sets the name of the place the line under the field carries.
   *
   * @param name The name, empty for none.
   */
  public setPlace(name: string): void {
    if (name === this.place) {
      return;
    }
    this.place = name;
    this.publish();
  }

  /**
   * Shows or hides the line under the field that says where the player is, and keeps the choice.
   *
   * @param show Whether the line shows.
   */
  public setCoordinates(show: boolean): void {
    if (show === this.coordinates) {
      return;
    }
    this.coordinates = show;
    writeSetting(MINIMAP_COORDINATES_KEY, show ? "on" : "off", this.store);
    this.publish();
  }

  /**
   * Sets the known places the field and the compass may draw, and tells the painters when they differ from the last.
   *
   * @param spots The places in tiles.
   */
  public setPlaces(spots: readonly MinimapPlaceSpot[]): void {
    const same = this.placesHeld.length === spots.length && this.placesHeld.every((held, index) => {
      const next = spots[index] as MinimapPlaceSpot;
      return held.handle === next.handle && held.x === next.x && held.y === next.y && held.name === next.name;
    });
    this.placesHeld = spots;
    if (!same) {
      this.publish();
      this.tellMovers();
    }
  }

  /**
   * The known places as last given.
   *
   * @returns The places; the same array until `setPlaces` is given another.
   */
  public places(): readonly MinimapPlaceSpot[] {
    return this.placesHeld;
  }

  /**
   * How many of the nearest known places the compass band points to, 0 is Off. The field draws every known place inside it whatever the choice; the choice is the band's alone.
   *
   * @returns A whole number from 0 to 12.
   */
  public compassPlaces(): number {
    return this.compassCount;
  }

  /**
   * Sets how many known places the compass shows, keeps the choice and tells the painters.
   *
   * @param count A whole number from 0 to 12; anything else is ignored.
   */
  public setCompassPlaces(count: number): void {
    if (!Number.isInteger(count) || count < 0 || count > COMPASS_PLACES_MOST || count === this.compassCount) {
      return;
    }
    this.compassCount = count;
    writeSetting(COMPASS_PLACES_KEY, String(count), this.store);
    this.tellMovers();
  }

  /**
   * Tells every painter to draw again.
   */
  private tellMovers(): void {
    for (const listener of this.movers) {
      listener();
    }
  }

  /**
   * Gives the map's pictures, or takes them away; the canvases are told, so they draw the ground at once.
   *
   * @param ground The pictures, or null.
   */
  public setGround(ground: MapPictureSource | null): void {
    this.ground = ground;
    for (const listener of this.movers) {
      listener();
    }
  }

  /**
   * Sets the player's position and lets the pictures compose the chunks they owe, the player's nearest first. The
   * snapshot is rebuilt only while party members, a pin or known places are held, since the player's own mark never
   * moves in its field and every other mark of the snapshot is placed relative to the player; the
   * painters are told when the position changed, a chunk landed or the other players' and creatures' marks changed.
   *
   * @param x Tiles across.
   * @param y Tiles down.
   */
  public setPosition(x: number, y: number): void {
    const moved = x !== this.px || y !== this.py;
    if (moved) {
      this.px = x;
      this.py = y;
      const partyMoved = this.partyMarks.moveSelf({ x, y });
      if (partyMoved || this.partyMarks.spots().length > 0 || this.pinHeld !== null || this.placesHeld.length > 0) {
        this.publish();
      }
    }
    const composed = this.ground !== null && this.ground.step(x, y);
    if (moved || composed || this.othersChanged) {
      this.othersChanged = false;
      for (const listener of this.movers) {
        listener();
      }
    }
  }

  /**
   * Empties the buffer of other players and creatures and hands it over to be refilled.
   *
   * @returns The buffer.
   */
  public beginOthers(): MinimapOthers {
    resetMinimapOthers(this.marksOthers);
    return this.marksOthers;
  }

  /**
   * Ends the refill: when the buffer's signature differs from the frame before's, the next `setPosition` tells the
   * painters, and a frame in which nothing in vision moved tells them nothing.
   */
  public endOthers(): void {
    if (this.marksOthers.signature !== this.heldOthers) {
      this.heldOthers = this.marksOthers.signature;
      this.othersChanged = true;
    }
  }

  /**
   * Sets the party members the field may draw.
   *
   * @param spots Their positions in tiles, as the `partyPositions` feed last gave them.
   * @param seats Their names, which the scene matches its drawn characters against.
   */
  public setParty(spots: readonly MinimapSpot[], seats: readonly PartySeat[] = []): void {
    const moved = this.partyMarks.setFed(spots, seats, { x: this.px, y: this.py });
    this.publish();
    if (moved) {
      for (const listener of this.movers) {
        listener();
      }
    }
  }

  /**
   * The names of the party members on the player's map.
   *
   * @returns The seats as last given.
   */
  public partySeats(): readonly PartySeat[] {
    return this.partyMarks.seats();
  }

  /**
   * Takes the party members drawn this frame; a mark that moves is published, and the painters hear it with the same
   * frame's `setPosition`.
   *
   * @param live The members drawn.
   */
  public setLiveParty(live: LiveParty): void {
    if (this.partyMarks.setLive(live, { x: this.px, y: this.py })) {
      this.othersChanged = true;
      this.publish();
    }
  }

  /**
   * The party members the field and the compass may draw, as last given.
   *
   * @returns Their positions in tiles; the same array until `setParty` is given another.
   */
  public partySpots(): readonly MinimapSpot[] {
    return this.partyMarks.spots();
  }

  /**
   * Sets the player's pin and tells the painters, which draw it the same frame.
   *
   * @param pin The pin in tiles, or null for none.
   */
  public setPin(pin: { readonly x: number; readonly y: number } | null): void {
    const held = this.pinHeld;
    if (held === pin || (held !== null && pin !== null && held.x === pin.x && held.y === pin.y)) {
      return;
    }
    this.pinHeld = pin === null ? null : { x: pin.x, y: pin.y };
    this.publish();
    for (const listener of this.movers) {
      listener();
    }
  }

  /**
   * The player's pin in tiles, or null for none.
   *
   * @returns The pin.
   */
  public pin(): { readonly x: number; readonly y: number } | null {
    return this.pinHeld;
  }

  /**
   * Keeps a zoom step.
   *
   * @param step The step.
   */
  private setStepTo(step: number): void {
    if (step === this.step) {
      return;
    }
    this.step = step;
    writeSetting(MINIMAP_STEP_KEY, String(step), this.store);
    this.publish();
  }

  /**
   * Builds the snapshot for the state now.
   *
   * @returns The snapshot.
   */
  private build(): MinimapSnapshot {
    return { step: this.step, canZoomIn: this.step < MINIMAP_SCALES.length - 1, canZoomOut: this.step > 0, party: this.partyMarks.held(), place: this.place, coordinates: this.coordinates, hud: this.field(MINIMAP_FIELD, false, this.pinHeld), map: this.mapSize === null ? null : this.field(this.mapSize, true) };
  }

  /**
   * Builds one field.
   *
   * @param size The field's size.
   * @param expanded Whether it is a Nearby field, the larger scale and the named marks.
   * @param pin The pin the field draws, null for none (Nearby draws none).
   * @returns The field.
   */
  private field(size: Size, expanded: boolean, pin: { readonly x: number; readonly y: number } | null = null): MinimapField {
    const scale = minimapScale(this.step, expanded);
    return { size, scale, marks: minimapMarks({ x: this.px, y: this.py }, this.partyMarks.spots(), size, scale, expanded, pin, this.placesHeld) };
  }

  /**
   * Rebuilds the snapshot and tells the listeners when it differs from the last.
   */
  private publish(): void {
    const next = this.build();
    const last = this.snapshot;
    if (next.step === last.step && next.party === last.party && next.place === last.place && next.coordinates === last.coordinates && sameMarks(next.hud.marks, last.hud.marks) && next.map?.size.width === last.map?.size.width && next.map?.size.height === last.map?.size.height && sameMarks(next.map?.marks ?? [], last.map?.marks ?? [])) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
